'use client'

/**
 * SponsoredSellerCard — the disclosed appliance-seller placement used on the
 * financing and warranty hubs (FRIDGE-1074, owner 09/17/2026: "sitewide ads
 * should include photos").
 *
 * The creative is ADDITIVE, not gating. The blog and collection AdCard hides
 * itself when its creative fails (FRIDGE-621 Real-or-Hidden) because it is a
 * pure ad slot; these cards also carry the page's own copy and disclosure, so
 * a dead CDN must not delete editorial content or leave a hole in the grid.
 * The image box is a fixed 6:5 (300×250 creative) so nothing shifts: the
 * network creative renders when it loads; if it errors, the box shows the
 * seller's first-party mark from /homepage-2026/retailers (always same-origin,
 * always loads). Never a broken-image icon, never an empty box.
 *
 * Copy rule (owner 09/18/2026): the card names the seller and what the reader
 * can do there; it never restates the seller's financing or warranty terms.
 */
import { useEffect, useRef, useState } from 'react'
import { RETAILER_MARKS } from '@/components/homepage-2026/retailer-mark'

export interface SponsoredSellerCardProps {
  trackingUrl: string
  seller: string
  kicker: string
  body: string
  cta: string
  imageUrl?: string
  imageAlt?: string
  /** Larger type for the single featured card above the grid. */
  featured?: boolean
}

export function sellerMarkPath(seller: string): string | null {
  const file = RETAILER_MARKS[seller.toLowerCase()]
  return file ? `/homepage-2026/retailers/${file}` : null
}

export function SponsoredSellerCard({ trackingUrl, seller, kicker, body, cta, imageUrl, imageAlt, featured = false }: SponsoredSellerCardProps) {
  const [creativeFailed, setCreativeFailed] = useState(false)
  // The creative's error listener is attached here rather than as an onError prop: an <img>
  // is non-interactive (jsx-a11y), and an image that failed BEFORE hydration never fires a
  // React onError — the complete/naturalWidth check catches that case too.
  const imgRef = useRef<HTMLImageElement>(null)
  const mark = sellerMarkPath(seller)
  const showCreative = Boolean(imageUrl) && !creativeFailed
  const visual = showCreative ? imageUrl! : mark
  useEffect(() => {
    const img = imgRef.current
    if (!img || !showCreative) return
    const fail = () => setCreativeFailed(true)
    if (img.complete && img.naturalWidth === 0) fail()
    img.addEventListener('error', fail)
    return () => img.removeEventListener('error', fail)
  }, [showCreative, visual])
  return (
    <a
      href={trackingUrl}
      target="_blank"
      rel="sponsored nofollow noopener"
      className={`relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-all hover:border-[#ff9951] hover:shadow-md ${featured ? 'sm:flex-row' : ''}`}
      data-sponsored-seller={seller}
      data-creative={showCreative ? 'network' : mark ? 'mark' : 'none'}
    >
      <span className="absolute right-3 top-3 z-10 rounded bg-white/95 px-2 py-0.5 text-[11px] font-medium text-gray-500 shadow-sm">
        Ad
      </span>
      {visual ? (
        <div
          className={`flex items-center justify-center border-b border-gray-100 bg-white p-4 ${featured ? 'sm:w-[240px] sm:shrink-0 sm:self-start sm:border-b-0 sm:border-r' : ''}`}
          style={{ aspectRatio: '6 / 5' }}
        >
          <img
            src={visual}
            alt={showCreative ? imageAlt || seller : `${seller} logo`}
            width={300}
            height={250}
            loading="lazy"
            decoding="async"
            className={showCreative ? 'max-h-full max-w-full object-contain' : 'max-h-[40%] max-w-[70%] object-contain'}
            ref={imgRef}
          />
        </div>
      ) : null}
      <div className={`flex flex-1 flex-col ${featured ? 'p-6' : 'p-5'}`}>
        <span className="mb-3 block font-mono text-[11px] font-bold tracking-[0.15em] text-[#ff9951]">{kicker}</span>
        <h3 className={`mb-1 font-bold tracking-tight text-gray-900 ${featured ? 'text-lg' : 'text-base'}`}>{seller}</h3>
        <p className="mb-4 flex-1 text-sm leading-relaxed text-gray-700">{body}</p>
        <span className="inline-flex items-center justify-center self-start rounded-lg bg-[#0469f0] px-4 py-2 text-sm font-semibold text-white">
          {cta} &rarr;
        </span>
      </div>
    </a>
  )
}
